import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ProfileRoleV2 } from '@ffd/api-client';
import { DropdownComponent, DropdownItem, InputComponent, ValidationIndicatorComponent } from '@libs/ui-lib';
import { ffdNotesRoutePrefix, multiPatternValidator, routes } from '@libs/utils';
import { RoleTranslationV2 } from '@libs/registration';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { BaaSignupFlowService } from '../baa-sign-up-flow.service';

const MIN_PASSWORD_LENGTH = 8;

@Component({
  selector: 'lib-baa-signup-account-details',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    InputComponent,
    RouterModule,
    SvgIconComponent,
    ValidationIndicatorComponent,
    DropdownComponent,
  ],
  templateUrl: './baa-sign-up-account-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BaaSignupAccountDetailsComponent implements OnInit {
  protected readonly service = inject(BaaSignupFlowService);
  private readonly translateService = inject(TranslateService);
  protected readonly routes = routes;
  protected readonly ffdNotesRoutePrefix = ffdNotesRoutePrefix;
  protected readonly isSubmitted = signal(false);
  protected readonly isPasswordVisible = signal(false);

  protected readonly roleOptions: DropdownItem<string>[] = Object.keys(ProfileRoleV2).map((key) => ({
    value: ProfileRoleV2[key as keyof typeof ProfileRoleV2],
    label: this.translateService.instant(RoleTranslationV2[key as keyof typeof RoleTranslationV2]),
  }));

  protected readonly form = new FormGroup({
    firstName: new FormControl<string>('', { validators: [Validators.required], nonNullable: true }),
    lastName: new FormControl<string>('', { validators: [Validators.required], nonNullable: true }),
    email: new FormControl<string>({ value: '', disabled: true }, { nonNullable: true }),
    role: new FormControl<ProfileRoleV2 | null>(null, { validators: [Validators.required] }),
    password: new FormControl<string>('', {
      validators: [
        Validators.required,
        Validators.minLength(MIN_PASSWORD_LENGTH),
        multiPatternValidator({ uppercase: /[A-Z]/, lowercase: /[a-z]/, digit: /\d/ }),
      ],
      nonNullable: true,
    }),
  });

  public ngOnInit(): void {
    if (this.service.isInvitationFlowSignal()) {
      this.form.controls.email.enable();
      this.form.controls.email.addValidators([Validators.required, Validators.email]);
      return;
    }

    const email = this.service.signerEmailSignal();
    if (email) {
      this.form.controls.email.setValue(email);
    }
  }

  protected onCheckIndicatorState(validation: 'minlength' | 'lowercase' | 'uppercase' | 'digit'): 'default' | 'error' | 'success' {
    if (this.form.controls.password.pristine) return 'default';
    const errors = this.form.controls.password.errors;
    if (validation === 'minlength' && (errors?.['required'] || errors?.['minlength'])) return 'error';
    if (errors?.['multiPattern']?.[validation]) return 'error';
    return 'success';
  }

  protected togglePasswordVisibility(): void {
    this.isPasswordVisible.update((v) => !v);
  }

  protected onSubmit(): void {
    this.isSubmitted.set(true);
    if (this.form.invalid) {
      this.form.controls.firstName.markAsDirty();
      this.form.controls.lastName.markAsDirty();
      this.form.controls.email.markAsDirty();
      this.form.controls.role.markAsDirty();
      this.form.controls.password.markAsDirty();
      return;
    }
    const { firstName, lastName, email, role, password } = this.form.getRawValue();
    this.service.saveAccountDetails({ firstName, lastName, role: role!, password, email });
  }
}
